import React, { useState } from 'react'
import { useDispatch } from 'react-redux'

import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import { RequestFormStyle } from '../styles'
import { setErrorNotification, setSuccessNotification } from '../../reducers/notificationReducer'
import { createPaper } from '../../reducers/papersReducer'
import useResourceSubmit from '../../hooks/useResourceSubmit'

import resourceService from '../../services/resources'
import { validateField, validateOptionalField, DESCRIPTION_MAX_LENGTH, validateRequiredResourceTags, collectTagData } from '../../utils/formValidation'
import ResourceTagsSection from '../shared/ResourceTagsSection'
import ResourceCheckboxes from '../shared/ResourceCheckboxes'
import TCAIREMFieldsSection from '../shared/TCAIREMFieldsSection'
import CoreResourceFields from '../shared/CoreResourceFields'
import ResourceDescriptionField from '../shared/ResourceDescriptionField'

const PaperForm = () => {

    const dispatch = useDispatch()
    const { isSubmitting, submit } = useResourceSubmit()

    const [name, setName] = useState('')
    const [nameError, setNameError] = useState(false)
    const [nameHelper, setNameHelper] = useState('')
    const [description, setDescription] = useState('')
    const [descriptionError, setDescriptionError] = useState(false)
    const [descriptionHelper, setDescriptionHelper] = useState('')
    const [authors, setAuthors] = useState('')
    const [authorsError, setAuthorsError] = useState(false)
    const [authorsHelper, setAuthorsHelper] = useState("As provided in paper's citation")
    const [link, setLink] = useState('')
    const [linkError, setLinkError] = useState(false)
    const [linkHelper, setLinkHelper] = useState('')
    const [date, setDate] = useState('')
    const [dateError, setDateError] = useState(false)
    const [dateHelper, setDateHelper] = useState('')
    const [doi, setDoi] = useState('')
    const [doiError, setDoiError] = useState(false)
    const [doiHelper, setDoiHelper] = useState('')
    const [contentTags, setContentTags] = useState([])
    const [medicalTags, setMedicalTags] = useState([])
    const [adminCategoryTags, setAdminCategoryTags] = useState([])
    const [experienceTags, setExperienceTags] = useState([])
    const [demographicTags, setDemographicTags] = useState([])
    const [showTagErrors, setShowTagErrors] = useState(false)
    const [volume, setVolume] = useState('')
    const [volumeError, setVolumeError] = useState(false)
    const [volumeHelper, setVolumeHelper] = useState('')
    const [journal, setJournal] = useState('')
    const [journalError, setJournalError] = useState(false)
    const [journalHelper, setJournalHelper] = useState('')
    const [pages, setPages] = useState('')
    const [pagesError, setPagesError] = useState(false)
    const [pagesHelper, setPagesHelper] = useState('')
    const [issue, setIssue] = useState('')
    const [issueError, setIssueError] = useState(false)
    const [issueHelper, setIssueHelper] = useState('')
    const [hasModel, setHasModel] = useState(false)
    const [modelLink, setModelLink] = useState('')
    const [searchDoi, setSearchDoi] = useState('')
    const [searchDoiError, setSearchDoiError] = useState(false)
    const [searchDoiHelper, setSearchDoiHelper] = useState('')
    const [isSeminalWork, setIsSeminalWork] = useState(false)
    const [inQuiz, setInQuiz] = useState(false)
    const [isTcairem, setIsTcairem] = useState(false)
    const [isPaid, setIsPaid] = useState(false)
    const [tcairemTags, setTcairemTags] = useState([])
    const [personaTags, setPersonaTags] = useState([])

    const validateOptional = (val, errorHandler, helperHandler, maxLength, fieldName) => {
        if (maxLength && val.length > maxLength) {
            errorHandler(true)
            helperHandler(`${fieldName} must be ${maxLength} characters or less (currently ${val.length})`)
            return false
        } else {
            errorHandler(false)
            helperHandler('')
            return true
        }
    }

    const reset = () => {
        setName('')
        setNameError(false)
        setNameHelper('')
        setDescription('')
        setDescriptionError(false)
        setDescriptionHelper('')
        setAuthors('')
        setAuthorsError(false)
        setAuthorsHelper('')
        setLink('')
        setLinkError(false)
        setLinkHelper('')
        setDate('')
        setDateError(false)
        setDateHelper('')
        setDoi('')
        setDoiError(false)
        setDoiHelper('')
        setContentTags([])
        setMedicalTags([])
        setAdminCategoryTags([])
        setExperienceTags([])
        setDemographicTags([])
        setShowTagErrors(false)

        setVolume('')
        setVolumeError(false)
        setVolumeHelper('')
        setJournal('')
        setJournalError(false)
        setJournalHelper('')
        setPages('')
        setPagesError(false)
        setPagesHelper('')
        setIssue('')
        setIssueError(false)
        setIssueHelper('')
        setHasModel(false)
        setModelLink('')
        setIsSeminalWork(false)
        setInQuiz(false)
        setIsTcairem(false)
        setIsPaid(false)
        setTcairemTags([])
        setPersonaTags([])
    }

    const handleSubmit = (event) => {
        event.preventDefault()
        setShowTagErrors(true)
        if (validateField(name, setNameError, setNameHelper, 500, 'Title') && 
            validateField(authors, setAuthorsError, setAuthorsHelper, 500, 'Authors') &&
            validateField(link, setLinkError, setLinkHelper) && 
            validateField(date, setDateError, setDateHelper) && 
            validateField(doi, setDoiError, setDoiHelper, 60, 'DOI') &&
            validateOptionalField(description, setDescriptionError, setDescriptionHelper, DESCRIPTION_MAX_LENGTH, 'Description') &&
            validateRequiredResourceTags({
                contentTags,
                adminCategoryTags,
                experienceTags,
                demographicTags,
                tcairemTags,
                personaTags,
                isTcairem,
            })
        ) {

            const data = {
                title: name,
                description: description,
                authors: authors,
                link: link,
                pub_date: date,
                doi: doi,
                has_model: hasModel,
                model_link: modelLink,
                is_seminal_work: isSeminalWork,
                in_quiz: inQuiz,
                is_tcairem: isTcairem,
                is_paid: isPaid,
                ...collectTagData({ contentTags, medicalTags, adminCategoryTags, experienceTags, demographicTags, tcairemTags, personaTags, isTcairem }),
            };


            // Validate optional fields before adding to data
            let optionalFieldsValid = true
            if(volume) {
                if(!validateOptional(volume, setVolumeError, setVolumeHelper, 40, 'Volume')) {
                    optionalFieldsValid = false
                }
                data.volume = volume
            }
            if(journal) {
                if(!validateOptional(journal, setJournalError, setJournalHelper, 100, 'Journal')) {
                    optionalFieldsValid = false
                }
                data.journal = journal
            }
            if(pages) {
                if(!validateOptional(pages, setPagesError, setPagesHelper, 20, 'Pages')) {
                    optionalFieldsValid = false
                }
                data.pages = pages
            }
            if(issue) {
                if(!validateOptional(issue, setIssueError, setIssueHelper, 50, 'Issue')) {
                    optionalFieldsValid = false
                }
                data.issue = issue
            }

            if(!optionalFieldsValid) {
                dispatch(setErrorNotification('Please fix field length errors before submission'))
                return
            }

            submit(createPaper(data), reset)
        } else {
            dispatch(setErrorNotification('Please fill all required fields before submission'))
        }
    }

    const handleSearch = async () => {
        if (searchDoi === '') {
            setSearchDoiError(true)
            setSearchDoiHelper('Please fill this field first!')
            return
        }
        setSearchDoiError(false)
        setSearchDoiHelper('')

        try {
            const res = await resourceService.retrievePaper(searchDoi)
            setName(res.message.title[0])
            setAuthors(res.message.author.reduce((prev, cur) => {
                if (prev === '') {
                    return `${cur.given} ${cur.family}`
                } else {
                    return prev + `, ${cur.given} ${cur.family}`
                }
            }, ''))
            if(res.message.created["date-time"]) setDate(res.message.created["date-time"].split('T')[0])
            setLink(res.message.resource.primary.URL)
            setDoi(res.message.DOI)
            if(res.message.volume) setVolume(res.message.volume)
            if(res.message['container-title']) setJournal(res.message['container-title'][0])
            if(res.message.page) setPages(res.message.page)
            if(res.message.issue) setIssue(res.message.issue)
            dispatch(setSuccessNotification('Paper Found'))
        } catch {
            dispatch(setErrorNotification('A paper could not be found with that DOI'))
        }

        setSearchDoi('')
    }

    return (
        <>
            <Box sx={RequestFormStyle.searchContainer}>
                <Box sx={RequestFormStyle.searchTextContainer}>
                    <Typography {...RequestFormStyle.searchText} gutterBottom>
                        Need help filling fields? You can fill fields automatically by searching with the paper DOI below.
                        Note you will still need to fill the key words.
                    </Typography>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            value={searchDoi}
                            onChange={e => setSearchDoi(e.target.value)}
                            label="DOI"
                            helperText={searchDoiHelper}
                            error={searchDoiError}
                        />
                    </Grid>
                    <Grid item xs={4} md={2}>
                        <Button {...RequestFormStyle.searchButton.props} sx={RequestFormStyle.searchButton.style} fullWidth onClick={()=>handleSearch()}>
                            Search DOI
                        </Button>
                    </Grid>
                </Grid>
            </Box>
            <br />
            <Container {...RequestFormStyle.container}>
                <Typography {...RequestFormStyle.title.props} gutterBottom sx={RequestFormStyle.title.style}>
                    Paper Form
                </Typography>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <CoreResourceFields
                            name={name} setName={setName} nameError={nameError} setNameError={setNameError} nameHelper={nameHelper} setNameHelper={setNameHelper}
                            authors={authors} setAuthors={setAuthors} authorsError={authorsError} setAuthorsError={setAuthorsError} authorsHelper={authorsHelper} setAuthorsHelper={setAuthorsHelper}
                            link={link} setLink={setLink} linkError={linkError} setLinkError={setLinkError} linkHelper={linkHelper} setLinkHelper={setLinkHelper}
                            date={date} setDate={setDate} dateError={dateError} setDateError={setDateError} dateHelper={dateHelper} setDateHelper={setDateHelper}
                            validate={validateField}
                            nameLabel="Title"
                            authorsLabel="Authors"
                            showCharCount={true}
                            nameMaxLength={500}
                            authorsMaxLength={500}
                        />
                        <ResourceDescriptionField
                            value={description} setValue={setDescription}
                            error={descriptionError} setError={setDescriptionError}
                            helper={descriptionHelper} setHelper={setDescriptionHelper}
                        />
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                value={doi}
                                onChange={e => setDoi(e.target.value)}
                                label="DOI"
                                required
                                error={doiError}
                                helperText={doiHelper || `${doi.length}/60 characters`}
                                onBlur={()=>validateField(doi, setDoiError, setDoiHelper, 60, 'DOI')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                value={volume}
                                onChange={e => setVolume(e.target.value)}
                                label="Volume"
                                error={volumeError}
                                helperText={volumeHelper || `${volume.length}/40 characters`}
                                onBlur={()=>validateOptional(volume, setVolumeError, setVolumeHelper, 40, 'Volume')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                value={issue}
                                onChange={e => setIssue(e.target.value)}
                                label="Issue"
                                error={issueError}
                                helperText={issueHelper || `${issue.length}/50 characters`}
                                onBlur={()=>validateOptional(issue, setIssueError, setIssueHelper, 50, 'Issue')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                value={journal}
                                onChange={e => setJournal(e.target.value)}
                                label="Journal"
                                error={journalError}
                                helperText={journalHelper || `${journal.length}/100 characters`}
                                onBlur={()=>validateOptional(journal, setJournalError, setJournalHelper, 100, 'Journal')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                value={pages}
                                onChange={e => setPages(e.target.value)}
                                label="Pages"
                                placeholder='Suggested Page Range'
                                error={pagesError}
                                helperText={pagesHelper || `${pages.length}/20 characters`}
                                onBlur={()=>validateOptional(pages, setPagesError, setPagesHelper, 20, 'Pages')}
                            />
                        </Grid>
                        <ResourceTagsSection
                            contentTags={contentTags} setContentTags={setContentTags}
                            medicalTags={medicalTags} setMedicalTags={setMedicalTags}
                            adminCategoryTags={adminCategoryTags} setAdminCategoryTags={setAdminCategoryTags}
                            experienceTags={experienceTags} setExperienceTags={setExperienceTags}
                            demographicTags={demographicTags} setDemographicTags={setDemographicTags}
                            required={true}
                            forceShowRequiredErrors={showTagErrors}
                        />
                        <ResourceCheckboxes
                            inQuiz={inQuiz} setInQuiz={setInQuiz}
                            isSeminalWork={isSeminalWork} setIsSeminalWork={setIsSeminalWork}
                        />
                        <TCAIREMFieldsSection
                            isTcairem={isTcairem} setIsTcairem={setIsTcairem}
                            isPaid={isPaid} setIsPaid={setIsPaid}
                            tcairemTags={tcairemTags} setTcairemTags={setTcairemTags}
                            personaTags={personaTags} setPersonaTags={setPersonaTags}
                            forceShowRequiredErrors={showTagErrors}
                        />
                        <Grid item xs={12} sm={6}>
                            <FormControl component="fieldset">
                                <FormLabel component="legend">Does the paper have an attached model?</FormLabel>
                                <RadioGroup row aria-label="hasModel" name="hasModel" value={hasModel} onChange={(e) => setHasModel(e.target.value === 'true')}>
                                    <FormControlLabel value={true} control={<Radio />} label="Yes" />
                                    <FormControlLabel value={false} control={<Radio />} label="No" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        {hasModel && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    value={modelLink}
                                    onChange={e => setModelLink(e.target.value)}
                                    label="Model URL"
                                    placeholder='Link to Model'
                                    required
                                />
                            </Grid>
                        )}

                        <Grid item xs={4}>
                            <Button sx={RequestFormStyle.button.style} {...RequestFormStyle.button.props} type='submit' disabled={isSubmitting}>Create Paper</Button>
                        </Grid>
                    </Grid>
                </form>
            </Container>
        </>
    )
}

export default PaperForm
